/**
 * 상인들.
 *
 *   물약상인 — 소모품
 *   잡화상   — 잡템 매입
 *   무기상인 / 방어구상인 — **등급별 완제품 판매** (일반 정착지 하급~최고급, 유리샘~전설)
 *
 * 상인이 조잡한 물건 하나만 놓고 있으면 존재 이유가 없다. 대장간이 재료까지 팔면서
 * 실제로 그렇게 됐고, 사용자가 "그럼 무기상인 역할은 뭐냐" 고 찾아냈다.
 * 위로 올라가는 사다리의 **완제품 쪽**을 상인이 맡는다.
 */
import {
  CRUDE_PRICE, GLASS_SHOP_ID, PREMIUM_SHOP_MAX_GRADE, RIFT_SHOP_ID, GRADES, JUNK_PRICE, NPCS, POTIONS, SHOP_MAX_GRADE,
  isShopGradeAt, settlementAt, shopGradePrice,
} from '../../world/map.js';
import { SLOT_META, slotBonus } from '../../world/gearnames.js';
import { S, esc } from '../../core/state.js';
import { adsStatus } from '../ads.js';
import { escapeFieldAt } from '../../world/escape-field.js';
import { nativeFieldGeometry } from '../../world/native-field.js';
import * as pets from './pets.js';
import * as alchemy from './alchemy.js';

/** 광고 한 번의 보상 (사용자 지정). ⚠ 서버가 붙으면 이 값도 서버에서 온다 */
const AD_REWARD = 1000000;
import { ascendRule, buyBtn, canSellGear, chanceNotice, dealFor, dealLeft, eqName, gearEdge, gearRow, gradeCol, lore, muted, npcHead, num, sec, sellGearBtn, slotKo, split , junkName,
  taxPct, taxNote, taxedPrice, withTax, junkSalePreview, junkPriceMultOf } from './common.js';

/**
 * 특가 한 줄의 값(UNI-Q190). 악세는 한 번에 하나를 사므로 **세금 포함 결제 금액**이다.
 * 물약은 개당 값이다 — 세금은 묶음 합계에 붙어(개당으로 나누면 1 은화 밑이 잘린다)
 * 실제 결제 금액은 묶음 구매 버튼에 적는다.
 */
const dealText = (d) => d.kind === 'acc'
  ? taxedPrice(d.price, S.you, d.was)
  : `<s>${num(d.was)}</s> <b style="color:var(--gold)">${num(d.price)}</b> <span class="qt">개당 · 세금 별도</span>`;

/**
 * 상점 — **물건을 보여준다. 사람 목록이 아니라.**
 *
 * 개편 전에는 이 화면이 **NPC 일곱 명의 목록**이었고, 물건을 보려면 [보기] 를
 * 눌러 다음 화면으로 가야 했다(2.0화면 + 한 단계). 그래서 물약 하나 사는 데
 * *상점 열기 → 스크롤 → 물약상인 찾기 → 보기 → 사기* 다섯 걸음이었다.
 * **그 목록에는 창고 관리인·대장장이·마권상처럼 물건을 안 파는 사람도 섞여 있었다.**
 *
 * 지금은 **분류를 위에 고정하고 물건을 바로 아래에 편다.** 두 걸음이다.
 * 사람 목록은 접어서 맨 아래에 남긴다 — 세계에서 직접 찾아가는 길이 여전히 있고,
 * "누가 무엇을 파는가" 를 알고 싶은 사람도 있다.
 */
const SHOP_GROUPS = [
  { id: 'potion', name: '잡화', children: [{ id: 'potion', name: '물약' }, { id: 'acc', name: '악세' }] },
  // 상점이 완제품으로 파는 부위는 서버 정본 SLOTS.shopSale(무기·갑옷)뿐 — 투구·장갑·신발·망토·반지는 상자깡(운장수)·드롭으로만 나온다(사용자 2026-09-08).
  // 전엔 강화 가능한 부위 전부를 탭으로 펴서 "다른 부위도 파는" 것처럼 보였다. 탭 이름도 인코딩 사고로 '?λ퉬' 가 박혀 있었다.
  { id: 'gear', name: '장비', children: Object.entries(SLOT_META)
    .filter(([, meta]) => meta.merchant && meta.enhance && meta.shopSale)
    .map(([id]) => ({ id, name: slotKo(id) })) },
  { id: 'alchemy', name: '연금술' },
  { id: 'gamble', name: '갬블' },
  { id: 'pet', name: '펫' },
  // 판매는 언제나 오른쪽 끝에 두어 찾는 시간을 줄인다.
  { id: 'sell', name: '팔기' },
];
// 개별 놀이는 허브에서 기존 NPC 패널로 연다. 상점의 장비 분류로 렌더링하지 않는다.
const LEAF_IDS = SHOP_GROUPS.flatMap((g) => (g.children || [{ id: g.id }]).map((c) => c.id));

export const gambleHubOpen = () => S.panel === 'shop' && S.shopTab === 'gamble';

function shopGroupFor(id) {
  return SHOP_GROUPS.find((g) => (g.children || [{ id: g.id }]).some((c) => c.id === id)) || SHOP_GROUPS[0];
}

function shopNav(cur) {
  const group = shopGroupFor(cur);
  const primary = SHOP_GROUPS.map((g) => {
    const first = (g.children || [{ id: g.id }])[0];
    const target = first.npc ? g.id : first.id;
    const active = g.id === group.id;
    return `<button class="${active ? 'on ' : ''}${g.id === 'sell' ? 'sell-tab' : ''}" onclick="act.shopTab('${target}')">${g.name}</button>`;
  }).join('');
  const secondary = group.children?.length
    ? `<div class="shop-subnav">${group.children.map((c) => c.npc
      ? `<button onclick="act.npc('${c.npc}')">${c.name}</button>`
      : `<button class="${c.id === cur ? 'on' : ''}" onclick="act.shopTab('${c.id}')">${c.name}</button>`).join('')}</div>`
    : '';
  return `<div class="shop-nav"><div class="shop-primary">${primary}</div>${secondary}</div>`;
}

export function renderShop(ctx, accRender) {
  const cur = LEAF_IDS.includes(S.shopTab) ? S.shopTab : 'potion';
  const d = S.deal;
  const head = shopNav(cur)
    /**
     * **오늘의 특가는 분류 바로 밑 한 줄.** 사냥터에서 돌아왔을 때 오늘만 싼 것이
     * 걸려 있으면 상점을 한 번 연다 — 찾아 들어가야 보이면 그 이유가 안 생긴다.
     * 남은 시간을 같이 적는다. "오늘" 이 안 보이면 그냥 싼 물건이다.
     */
    + (d && cur !== 'gamble'
      ? `<div class="dealline">★ <b>${esc(d.name)}</b> ${dealText(d)} `
        + `<span class="qt">${Math.round(d.off * 100)}% · ${dealLeft()} 남음</span> `
        + `<button onclick="act.shopTab('${d.kind === 'acc' ? 'acc' : 'potion'}')">보기</button></div>`
      : '');

  const body = cur === 'potion' ? renderPotion(ctx)
    : cur === 'sell' ? renderSell(ctx)
    // 바깥 상점이 이미 split을 소유한다. 악세 화면을 또 split하면 중첩 scroller가 되어
    // 통합 상점의 악세 탭만 위아래 스크롤이 멈춘다.
    : cur === 'acc' ? accRender(ctx, { embedded: true })
    : cur === 'alchemy' ? alchemy.render(ctx, { embedded: true })
    // 갬블·펫은 각자 패널이 따로 있다 — 여기선 진입만 모으고 실제 구매/베팅과 제약은
    // 그 패널이 그대로 판정한다(새 경제 경로 없음). 기존 NPC 직접 진입도 유지된다.
    : cur === 'gamble' ? gambleHub(ctx)
    : cur === 'pet' ? pets.render(ctx, { embedded: true })
    : renderGear({ ...ctx, slot: cur });

  return split(head, body + people(), cur === 'alchemy' ? alchemy.selectedAction(ctx) : '');
}

/** 마을 사람들 — 접어서 남긴다. 직접 찾아가는 길이 여전히 있다 */
function people() {
  return `<details class="lore"><summary>마을 사람들 <span class="qt">${NPCS.length}명</span></summary><div class="in">`
    + muted('세계에서 직접 찾아가 말을 걸어도 된다.')
    + NPCS.map((n) => `<div class="item"><span class="nm">${esc(n.name)}<br><span class="qt">${esc(n.line)}</span></span>
      <button onclick="act.npc('${n.id}')">보기</button></div>`).join('')
    + '</div></details>';
}

/**
 * **갬블 허브** — 공동 잭팟을 먼저 보고 기존 놀이와 NPC를 고른다.
 *
 * ⚠ **새 경제 경로를 만들지 않는다** — 버튼은 기존 패널을 열 뿐이고(`act.npc`), 실제
 * 뽑기·베팅·구매와 마을/쿨 제약은 그 패널이 그대로 판정한다(rule5 표면 안 넓힘).
 * 이름·대사는 `NPCS` 에서 뽑아 세계의 NPC 와 안 갈라지게 한다.
 */
function gambleHub({ town }) {
  const fund = S.slot?.fund;
  const known = typeof fund === 'number' && Number.isFinite(fund);
  const why = S.fieldReadFail?.slotGet || (!S.connected ? '서버에 연결한 뒤 다시 확인해 달라.' : S.slot && !known ? '금액을 불러오지 못했다. 다시 확인해 달라.' : '');
  let h = `<div class="racepot gamblepot"><span>👑 공동 잭팟</span><b>${!town ? '마을에서 확인' : why ? '금액 확인 못함' : known ? `${num(fund)} 은화` : '불러오는 중…'}</b>
    <small>경주·복권·블랙잭·슬롯이 함께 쓰는 기금</small>
    ${town && why ? `<small role="status">${esc(why)}</small>` : ''}
    ${town ? '<button class="ghost" onclick="act.shopTab(\'gamble\')">다시 확인</button>' : ''}</div>`;
  h += chanceNotice();
  for (const [panel, label] of [['gamble', '운장수 · 장비 뽑기'], ['race', '슬라임 경주'], ['lottery', '복권'], ['slot', '슬롯머신'], ['blackjack', '블랙잭']]) {
    const n = NPCS.find((q) => q.panel === panel);
    if (!n) continue;
    h += `<div class="item"><span class="nm">${label}<br><span class="qt">${esc(n.name)}${panel === 'blackjack' ? ' · 유리샘 마을' : ''}</span></span>
      <button onclick="act.npc('${n.id}')">열기</button></div>`;
  }
  return h;
}

// Direct pet-tab rendering is handled by pets.js.

/** 마을 사람 목록 (옛 진입점 — NPC 를 직접 눌렀을 때) */
export function renderDirectory() {
  /**
   * **오늘의 특가는 여기 맨 위다.**
   *
   * 마을은 지금 "필요한 걸 사는 곳" 이지 **들를 이유가 있는 곳**이 아니다.
   * 사냥터에서 돌아왔을 때 오늘만 싼 것이 하나 걸려 있으면 상점을 한 번 연다.
   * 그러려면 **찾아 들어가야 보이는 자리**에 두면 안 된다 — 상점을 열자마자 보이는
   * 첫 줄이라야 "들를 이유" 가 된다.
   *
   * **남은 시간을 같이 적는다.** "오늘" 이라는 것이 안 보이면 그냥 싼 물건이고,
   * 오늘 사야 할 이유가 생기지 않는다.
   */
  let h = '';
  const d = S.deal;
  if (d) {
    h += sec('오늘의 특가',
      `<div class="item"><span class="nm">${esc(d.name)}`
      + `<br><span class="qt">${Math.round(d.off * 100)}% 싸다 · ${dealLeft()} 남음</span></span>`
      + `<span class="qt">${dealText(d)}</span>`
      + `<button onclick="act.npc('${d.kind === 'acc' ? 'acc' : 'potion'}')">보러 가기</button></div>`);
  }
  h += sec('마을 사람들', muted('직접 찾아가 말을 걸어도 되고, 여기서 골라도 된다.'));
  for (const n of NPCS) {
    h += `<div class="item"><span class="nm">${esc(n.name)}<br><span class="qt">${esc(n.line)}</span></span>
      <button onclick="act.npc('${n.id}')">보기</button></div>`;
  }
  return h;
}

/**
 * 좌표 판정표 D14 — 2.0 사냥터 소켓 위에서는 좌표가 사냥터 좌표다(1.0 지도보다 넓힌 땅은 같은 숫자의 다른 곳). 더보기 → 마을 사람들로
 * 여기서도 상인 창이 열리므로, 자리 문구는 1.0 좌표 함수가 아니라 사냥터 신원으로 정한다. 사냥터가 아니면 null.
 */
const fieldId = () => nativeFieldGeometry()?.fieldId ?? null;

/** 물약상인 */
export function renderPotion({ y, at }) {
  // D14 — 분지·지옥 사냥터 안이면 보급지 물약 목록(서버 `field-scroll.ts` blockedField 와 같게 사냥터 신원이 이긴다). 다른 사냥터는 보통 목록
  const field = fieldId();
  const supply = !!y && (field ? field === 'surface:escape' || field === 'surface:hell' : escapeFieldAt(y.x, y.y));
  let h = npcHead() + `<div class="sec"><h3>${supply?'분지 보급지 · 회복 물약':'물약과 주문서'}</h3>`;
  if(supply)h+=muted('보급지 안에서는 공격받지 않는다. 귀환·순간이동은 막혀 있으므로 서쪽 출구까지 걸어가야 한다.');
  // 창고 안내는 여기 한 번만 — 버튼마다 달면 줄이 밀리고 같은 문장이 아홉 번 선다(buyBtn 의 quiet)
  for (const s of POTIONS.filter(s=>!supply||s.id!=='scroll')) {
    /**
     * **적힌 값이 실제로 빠져나가는 값이어야 한다.**
     *
     * 서버는 특가 물건을 특가로 판다(`dealPrice`). 화면이 정가를 적어두면 살 때
     * 은화가 덜 나가는데, 좋은 쪽이라도 **화면이 거짓말한 것**이고 "왜 값이 다르지"
     * 로 돌아온다. 살 수 있는지 판정(`buyBtn`)도 같은 값을 봐야 특가 덕에 살 수
     * 있는 물건이 회색으로 막히지 않는다.
     */
    const d = dealFor('consum', s.id);
    const price = d ? d.price : s.price;
    // UNI-Q195 — 1개 버튼. 첫 걸음이 「물약을 한 병이라도 사 보자」인데 버튼이 10개·100개뿐이라 시작 은화로는 살 수 없었다.
    // 버튼이 셋이라 이름 칸 안 아래 줄에 둔다(모바일 폭). 줄은 그대로 `<div class="item">` 한 칸이다 — 안에 div 를 넣으면
    // 「대상과 행동이 같은 줄」 검사(check-panels)가 줄을 거기서 끊어 읽고, :has() 는 지원 안 되는 브라우저가 있어 안 쓴다
    // 개당 값은 설명 줄 끝에 둔다 — 오른쪽 칸으로 두면 그 폭만큼 버튼 줄이 좁아져 390px 휴대폰에서 1·10·100개가 두 줄로 밀린다.
    // 이름 줄에 따로 두면 이 칸의 .qt 가 제 줄을 차지해 이름·개당·빈 줄·설명으로 네 줄이 된다(운영 화면 실측)
    h += `<div class="item"><span class="nm">${s.name}${d ? ' <b style="color:var(--gold)">오늘의 특가</b>' : ''}`
      + `<br><span class="qt">${s.desc} · 개당 ${d ? `<s>${s.price}</s> <b style="color:var(--gold)">${price}</b>` : price}</span>
      <span class="potbuy">${[1, 10, 100].map((n) => `<span class="potbtn">${
        buyBtn(`${n}개 · ${num(withTax(price * n, y))}`, `act.buy('${s.id}',${n})`, { at, need: 'buy', y, price: withTax(price * n, y), quiet: true })}</span>`).join('')}</span></span></div>`;
  }
  // 묶음 AB — 세금 안내는 목록 **아래** 한 번. 위에 두면 폰 가로 첫 화면이 안내 문단이라 살 물건이 하나도 안 보였다
  h += muted(`구매 버튼의 금액은 왕도 세금 ${taxPct(y)}%를 포함한 실제 결제 금액이다. 가방 은화가 모자라면 부족분은 창고에서 낸다.`);
  return h + `</div>`;
}

/** 잡화상 — 잡템 매입 */
/** Google H5 광고 상태와 보상형의 안전 경계를 같은 자리에서 말한다. */
function googleAds() {
  const ad = adsStatus();
  const ready = ad.phase === 'ready';
  const label = ready ? '광고 시청 가능'
    : ad.phase === 'loading' ? 'Google 광고 확인 중…'
      : ad.phase === 'disabled' ? '광고 준비 중'
        : ad.phase === 'error' ? '광고 연결 실패 · 게임은 정상 이용 가능'
          : 'Google 광고 준비 중';
  return sec('광고와 운영 응원',
    `<div class="kv"><span>연결 상태</span><b>${label}</b></div>`
    + `<div class="row"><button class="wide"${!ready || S.busy === 'ad' ? ' disabled' : ''} onclick="act.adSupport()">`
    + `${S.busy === 'ad' ? '광고 여는 중…' : '응원 광고 보기'}</button></div>`
    + muted('보상 없는 선택 광고다. 광고가 없거나 차단돼도 게임은 멈추지 않는다.')
    + `<div class="kv"><span>보상형 예정</span><b>${num(AD_REWARD)} 은화 / 1회</b></div>`
    + `<div class="row"><button class="wide" disabled>1,000,000 은화 받기 · 준비 중</button></div>`
    + muted('은화 보상 광고는 준비 중이다. 현재 응원 광고에는 보상이 없다.'));
}

export function renderSell({ y, at, j, eq = [] }) {
  /**
   * UNI-Q190 — 받는 돈은 서버 `sell()` 과 같은 순서로 센다: 합계 → 악세 흥정 보너스(`junkSaleValue`,
   * 한 번 반올림) → 왕도 세금 공제. 전에는 합계만 적어서 세금·보너스가 붙은 실제 수령액과 달랐다.
   */
  const { base, gross, tax } = junkSalePreview(y, j);
  const jk = Object.keys(j).filter((k) => JUNK_PRICE[k]);
  /**
   * **「팔기」 칸에서 장비도 판다.** 10-22 개편 뒤 이 칸은 가방에서 고른 장비 하나만 보여서,
   * 팔 때마다 가방으로 돌아가 다시 골라야 했다(2026-10-25 사용자 신고). 착용 중·귀속·신전 반지처럼
   * 팔 수 없는 장비는 목록에 넣지 않는다 — 줄마다 누르면 바로 팔리고 다음 줄이 그 자리에 온다.
   */
  const forSale = eq.filter((e) => canSellGear(e, y.inv.equipped));
  let junk = `<div class="sec"><h3>잡템 판매 <em>${jk.length}종</em></h3>`
    + `<div class="ux-facts"><div class="ux-fact"><small>전부 팔면 받는 은화</small><b class="ux-number">${num(gross - tax)}</b></div>`
    + `<div class="ux-fact"><small>왕도 세금 ${taxPct(y)}% 공제</small><b class="ux-number">${num(tax)}</b><small>은화</small></div></div>`
    + `<div class="item"><span class="nm">잡템 전부 <span class="qt">${jk.length}종 판매</span></span>${buyBtn('전부 팔기', 'act.sell()', { at, need: 'sell', more: base > 0 ? [] : ['팔 잡템 없음'] })}</div>`;
  if (jk.length) junk += `<details class="lore" id="shop-junk-stock"><summary>팔리는 물건 ${jk.length}종 · 금액 상세</summary><div class="in">`;
  junk += jk.length
    ? jk.map((k) => `<div class="item"><span class="nm">${junkName(k)}</span>
        <span class="qt">×${j[k]} · ${num(Math.round(JUNK_PRICE[k] * j[k] * junkPriceMultOf(y)))} 은화</span></div>`).join('')
    : muted('팔 잡템이 없다.');
  if (base > 0) {
    junk += `<div class="kv"><span>판매가 합계</span><b>${num(base)} 은화</b></div>`
      + (gross > base ? `<div class="kv"><span>악세 흥정 보너스</span><b>+${num(gross - base)} 은화</b></div>` : '')
      + (tax > 0 ? `<div class="kv"><span>왕도 세금 ${taxPct(y)}%</span><b>−${num(tax)} 은화</b></div>` : '');
  }
  if (jk.length) junk += '</div></details>';
  junk += '</div>';
  const gearSale = `<div class="sec"><h3>장비 판매 <em>${forSale.length}점</em></h3>`
    + (forSale.length
      ? forSale.map((e) => `<div class="item gd" ${gearEdge(e)}><span class="nm">${eqName(e)}</span>${sellGearBtn(e, y, at)}</div>`).join('')
        + muted('판매 버튼의 은화는 세금 공제 후 실제 수령액이다. 장비를 팔면 되돌릴 수 없다. 착용 중인 장비는 벗은 뒤 여기에 나온다.')
      : muted('팔 수 있는 장비가 없다. 착용 중·귀속·빌린 장비는 팔 수 없다.'))
    + '</div>';
  // 팔 잡템이 없으면 장비 목록을 먼저 보인다 — 빈 잡템 칸이 첫 화면을 차지하면 장비가 화면 아래로 밀린다(390폭 실측)
  let h = npcHead() + (base > 0 || !forSale.length ? junk + gearSale : gearSale + junk);
  h += `<details class="lore" id="shop-support"><summary>광고로 운영 응원하기</summary><div class="in">${googleAds()}</div></details>`;
  if ((j.shard || 0) > 0) {
    h += sec('강화석', muted(`강화석은 사지 않는다. <b>대장간</b>에서 장비를 만들거나 강화에 쓴다. (보유 ${j.shard}개)`));
  }
  return h;
}

/**
 * 몫의 이름표. **부위가 아니라 몫에 단다** — 부위는 늘지만 몫의 종류는
 * `slotBonus` 가 돌려주는 칸이 전부다. 모르는 칸은 키 그대로 보여준다(조용히 빠지는 것보다 낫다).
 */
const STAT_KO = { atk: '공격', def: '방어', move: '이동', cap: '적재' };

/**
 * 조잡 장비의 `+0` 수치. **조잡은 핵심 둘에만 있다**(14.64) — 그래서
 * 표에 없는 부위는 조잡을 안 판다. 값은 서버 `crudeWeaponAtk(0)`·`crudeArmorDef(0)` 이다.
 */
const CRUDE_STAT = { weapon: 3, armor: 1 };
const crudeStat = (slot) => CRUDE_STAT[slot] ?? 0;

/** 무기상인 / 방어구상인 — 완제품 판매 */
export function renderGear({ y, at, eq, slot }) {
  const ko = slotKo(slot);
  // D14 — 사냥터 안은 어느 정착지도 아니다(`where()` 가 'out' 인 것과 같은 답) — 넓힌 땅이 유리샘 좌표와 겹쳐도 그 상인 목록을 펴지 않는다
  const settlement = fieldId() ? '' : settlementAt(y.x, y.y, 2)?.id || '';
  const glass = settlement === GLASS_SHOP_ID;
  const rift = settlement === RIFT_SHOP_ID; // 14.551 — 비늘바람 마을은 영웅까지
  const topGrade = PREMIUM_SHOP_MAX_GRADE[settlement] ?? SHOP_MAX_GRADE;
  /**
   * ⚠⚠ **여기가 `slot === 'weapon' ? weaponAtk : armorDef` 였다.**
   *
   * 지금은 상인이 무기·갑옷만 팔아서 맞았지만, `FI` 로 새 부위를 상점에 올리는
   * 순간 **장갑이 방어력으로, 신발이 방어력으로** 표시된다. 부위가 늘 때
   * **조용히 틀리는** 자리이고, 그게 이 화면에서 이미 한 번 난 사고다.
   *
   * ★ 부위가 무엇을 주는지는 **표가 안다.** 여기서 가르지 않는다.
   */
  const gives = (SLOT_META[slot] || {}).gives || 'atk';
  const stat = (plus, blessed, g) => {
    const v = slotBonus(slot, g, plus, blessed)[gives] || 0;
    return gives === 'move' ? `+${(v * 100).toFixed(0)}%` : num(Math.round(v));
  };
  const statKo = STAT_KO[gives] || gives;

  /**
   * `GY2` — 방어구상인은 **몸에 입는 장비 전부**를 매입한다.
   *
   * 이전 화면은 `e.slot === 'armor'` 한 종류만 폈다. 투구·장갑·신발·망토도 서버는
   * 팔 수 있었지만 가방 격자에서 하나를 고르고 → 상세 시트를 열고 → 팔기를 눌러야 했다.
   * "판매 가능"과 "판매하기 쉽다"는 다른 일이다. 사냥 뒤 정리하는 화면에서는
   * 방어구를 부위별 목록으로 한꺼번에 펴고 각 줄에서 바로 판다.
   *
   * 부위를 손으로 적지 않는다. `SLOT_META.merchant`가 같은 강화 장비를 도출한다.
   * 다음 방어구 부위가 생기면 정본의 상인 분류만 적고 이 목록과 검사도 저절로 따라온다.
   */
  const merchant = SLOT_META[slot]?.merchant;
  const saleSlots = Object.keys(SLOT_META)
    .filter((id) => SLOT_META[id].merchant === merchant && SLOT_META[id].enhance);
  const mine = eq.filter((e) => saleSlots.includes(e.slot));
  const ownedTitle = saleSlots.length > 1 ? '내 방어구 · 바로 판매' : `내 ${ko} · 바로 판매`;
  let owned = `<div class="sec"><h3>${ownedTitle}</h3>`;
  // 가방에서 눌러 본 장비를 여기 위에 고정하지 않는다 — 닫을 길이 없어 사라지지 않는 잔상이 됐다(2026-10-25 신고).
  if (!mine.length) owned += muted('없다.');
  for (const part of saleSlots) {
    const rows = mine.filter((e) => e.slot === part);
    if (!rows.length) continue;
    owned += `<div class="kv"><span>${slotKo(part)}</span><b>${rows.length}점</b></div>`
      + rows.map((e) => gearRow(e, y, at)).join('');
  }
  owned += `</div>`;

  // 사냥 뒤 정리가 구매보다 잦다. 내 물건과 판매 버튼을 첫 화면에 두고 상품을 그 뒤에 둔다.
  const guide = lore('등급이란', `
    구매한 장비의 강화 수치는 <b>+0</b>이다. 안전수치까지는 은화로 확정 강화하며,
    이후에는 <b>강화석·머지</b>로 강화한다. ${ascendRule()}<br>
    ${glass
      ? `유리샘 상인은 <b>전설까지</b> 취급한다. 영웅 ${num(withTax(shopGradePrice(5, GLASS_SHOP_ID), y))} · 전설 ${num(withTax(shopGradePrice(6, GLASS_SHOP_ID), y))} 은화(세금 포함)로 등급마다 가격이 4배다.`
      : rift
        ? `비늘바람 상인은 <b>영웅까지</b> 취급한다. 영웅 ${num(withTax(shopGradePrice(5, RIFT_SHOP_ID), y))} 은화(세금 포함). 전설은 유리샘 마을에서 판다.`
        : '이 마을 상인은 <b>최고급까지만</b> 취급한다. 비늘바람 마을에서는 영웅까지, 유리샘 마을에서는 전설까지 판다.'}`)
    + (SLOT_META[slot]?.merchant === 'wear' ? lore('부위별 판매처', '이 화면에서 사는 완제품은 <b>갑옷</b>이다. 투구·장갑·신발·망토는 운장수 또는 사냥에서 얻으며, 보유 중인 방어구는 위 목록에서 바로 팔 수 있다.') : '');
  let h = npcHead() + owned + `<div class="sec"><h3>완제품 ${ko} <em>구매 시 +0</em></h3>`;
  h += `<div class="item"><span class="nm"><span class="crude">조잡한 ${ko}</span>
      <span class="qt">${statKo} ${crudeStat(slot)} · 머지 불가</span></span>
    <span class="qt">${taxedPrice(CRUDE_PRICE, y)}</span>
    ${buyBtn('사기', `act.buyGear('${slot}')`, { at, need: 'buyGear', y, price: withTax(CRUDE_PRICE, y) })}</div>`;
  for (const g of GRADES) {
    if (!isShopGradeAt(g.id, settlement)) continue;
    const price = shopGradePrice(g.id, settlement);
    h += `<div class="item"><span class="nm" style="color:${gradeCol(g.id)}">${g.name} ${ko}
        <span class="qt">${statKo} ${stat(0, 0, g.id)} · 안전 +${g.safe} · +10 ${statKo} ${stat(10, 0, g.id)}</span></span>
      <span class="qt">${taxedPrice(price, y)}</span>
      ${buyBtn('사기', `act.buyItem('${slot}',${g.id})`, { at, need: 'buyItem', y, price: withTax(price, y) })}</div>`;
  }
  h += `</div>` + taxNote(y) + guide + lore('상점에서 팔지 않는 등급',
    GRADES.filter((g) => g.id > topGrade)
      .map((g) => `<span style="color:${gradeCol(g.id)}">${g.name}</span> ${statKo} ${stat(0, 0, g.id)}~${stat(10, 0, g.id)}`)
      .join(' · ') + '<br>네임드·<b>구역 보스</b> 드롭 또는 승급으로 얻는다. 궁극은 +10 이후에도 강화할 수 있다.');

  return h;
}
